import type { MediaData, MediaFolder } from "@/lib/config/types"

const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? ""
/** Matches codegen Media API: GET/POST `/media/folders` (proxied as `/api/media/folders`). */
const API_MEDIA_FOLDERS_PREFIX = `${BASE_URL}/api/media/folders`

function getAuthHeaders(): HeadersInit {
  const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.message || err.error || `Request failed: ${response.status}`)
  }
  return response.json()
}

/** GET — list folders (codegen: `foldersList`) */
export const mediaFolderList = async (
  parent?: number | null,
  search?: string,
  domain?: string,
): Promise<{ data: MediaFolder[] }> => {
  const params = new URLSearchParams()
  if (parent !== undefined && parent !== null) params.set("parent", String(parent))
  if (search) params.set("search", search)
  if (domain) params.set("domain", domain)

  const url = `${API_MEDIA_FOLDERS_PREFIX}${params.toString() ? `?${params}` : ""}`
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  })
  return handleResponse<{ data: MediaFolder[] }>(response)
}

/** GET — folder by id (codegen: `foldersDetail`) */
export const mediaFolderGet = async (id: number, domain?: string): Promise<{ data: MediaFolder }> => {
  const params = new URLSearchParams()
  if (domain) params.set("domain", domain)
  const url = `${API_MEDIA_FOLDERS_PREFIX}/${id}${params.toString() ? `?${params}` : ""}`
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  })
  return handleResponse<{ data: MediaFolder }>(response)
}

/** POST — create folder (codegen: `foldersCreate`) */
export const mediaFolderCreate = async (data: {
  name: string
  parent?: number | null
  domain: string
}): Promise<{ data: MediaFolder }> => {
  const response = await fetch(API_MEDIA_FOLDERS_PREFIX, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  })
  return handleResponse<{ data: MediaFolder }>(response)
}

/** PUT — update folder (codegen: `foldersUpdate`) */
export const mediaFolderUpdate = async (
  id: number,
  data: { name?: string; parent?: number | null; domain?: string },
): Promise<{ data: MediaFolder }> => {
  const params = new URLSearchParams()
  if (data.domain) params.set("domain", data.domain)
  const url = `${API_MEDIA_FOLDERS_PREFIX}/${id}${params.toString() ? `?${params}` : ""}`
  const response = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ 
      name: data.name, 
      parent: data.parent,
      domain: data.domain,
    }),
  })
  return handleResponse<{ data: MediaFolder }>(response)
}

/** DELETE — folder (codegen: `foldersDelete`) */
export const mediaFolderDelete = async (id: number, domain?: string): Promise<void> => {
  const params = new URLSearchParams()
  if (domain) params.set("domain", domain)
  const url = `${API_MEDIA_FOLDERS_PREFIX}/${id}${params.toString() ? `?${params}` : ""}`
  const response = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.message || `Delete failed: ${response.status}`)
  }
}

/** GET — files in folder (codegen: `foldersFilesList`); items align with app `MediaData`. */
export const mediaFolderListFiles = async (
  id: number,
  page = 1,
  pageSize = 50,
  domain?: string,
): Promise<{ data: MediaData[]; meta?: unknown }> => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sort: "createdAt:desc",
  })
  if (domain) params.set("domain", domain)
  const response = await fetch(`${API_MEDIA_FOLDERS_PREFIX}/${id}/files?${params}`, {
    method: "GET",
    headers: getAuthHeaders(),
  })
  return handleResponse<{ data: MediaData[]; meta?: unknown }>(response)
}
